import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Email validation lives here rather than on the input, because the field must be a plain text
 * input: it sits inside the Shopify product form, and a `type="email"` or `required` control there
 * participates in that form's native constraint validation and can block Add to Cart. See the note
 * on the input in snippets/bnq-notify-when-available.liquid.
 *
 * Deliberately permissive — one @, something either side, a dot in the domain. Address validity is
 * ultimately proven by delivery, and over-strict patterns reject real addresses.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * BONQUERÉ — Notify When Available
 * BNQ-WEB-0394 · Sprint 3.4.13 EXT · DEC-0166
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE ─────────────────────────────────────────────────
 * Success is reported ONLY after a real 2xx response from a configured endpoint.
 *
 * There is no localStorage fallback, no optimistic confirmation, no queue-for-later, and no
 * silent no-op that leaves a checkmark on screen. If `data-endpoint` is empty — which is the
 * current state until the owner-approved provider is connected — the component says so plainly
 * and stops. A customer must never be told they will be emailed when nothing recorded the request.
 *
 * ── WHAT IT DOES HANDLE ───────────────────────────────────────────────────────────────────────
 * Disclosure + focus management, email validation tied to the field, in-flight locking against
 * double submission, and the full state set: idle, invalid, submitting, success, duplicate (409),
 * error, unconfigured. Variant tracking is NOT handled here — the whole component is re-rendered
 * by Liquid on every variant change (see the snippet's header note), so there is no stale-variant
 * failure mode to guard against.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} submitLabel
 * @property {HTMLElement} status
 *
 * @extends Component<Refs>
 */
export class BnqNotify extends Component {
  requiredRefs = ['trigger', 'panel', 'email', 'submit', 'status'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {boolean} */
  #inFlight = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const { trigger, submit, email } = this.refs;

    trigger.addEventListener('click', this.#toggle, { signal });
    submit.addEventListener('click', this.#handleSubmit, { signal });

    // Enter in the field submits, matching the affordance of a real form without nesting one
    // (this markup lives inside the Shopify product <form>).
    email.addEventListener('keydown', this.#handleKeydown, { signal });

    // Clearing the error as soon as the customer starts correcting it keeps the message honest —
    // a stale "invalid email" beside a now-valid address is its own small lie.
    email.addEventListener('input', this.#clearValidation, { signal });

    this.addEventListener('keydown', this.#handleEscape, { signal });

    // Horizon re-renders the section on every variant change and hands the new markup over on this
    // event. Listening for it — rather than replacing the DOM — is what lets the native Add to Cart
    // button and all of its refs stay untouched, which is the constraint that the first
    // implementation violated (see blocks/add-to-cart.liquid).
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @returns {string | null} The variant id the surrounding product form would submit. */
  get #formVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.closest('form')?.querySelector('input[name="id"]'));
    return input?.value || null;
  }

  get #state() {
    return this.dataset.state ?? 'idle';
  }

  set #state(value) {
    this.dataset.state = value;
  }

  #toggle = () => {
    const { trigger, panel, email } = this.refs;
    const open = trigger.getAttribute('aria-expanded') === 'true';

    trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.hidden = open;

    // Focus moves into the panel on open so keyboard and screen-reader users land on the field
    // rather than having to hunt for it; it returns to the trigger on close so the tab position
    // is never lost.
    if (open) {
      trigger.focus();
    } else {
      email.focus();
    }
  };

  /**
   * Re-synchronises with the newly rendered variant.
   *
   * Everything is transcribed FROM LIQUID rather than derived here: the freshly rendered
   * `bnq-notify` in the incoming section HTML already carries the correct eligibility, variant id
   * and variant title, computed by the same rules as the first paint. Deriving them in JS would
   * mean two sources of truth for "is this variant restockable", and they would eventually drift.
   *
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const incoming = event.detail?.data?.html?.querySelector('bnq-notify');

    // No Notify in the new markup means the new variant is not restockable.
    if (!incoming) {
      this.dataset.eligible = 'false';
      return;
    }

    const changed = incoming.dataset.variantId !== this.dataset.variantId;

    this.dataset.eligible = incoming.dataset.eligible ?? 'false';
    this.dataset.variantId = incoming.dataset.variantId ?? '';
    this.dataset.variantTitle = incoming.dataset.variantTitle ?? '';

    if (!changed) return;

    // A new variant is a new request. Carrying a success or error message across would attach the
    // previous variant's outcome to this one — collapse the panel and start clean instead.
    const { trigger, panel, status, email } = this.refs;

    this.#state = 'idle';
    status.textContent = '';
    email.removeAttribute('aria-invalid');
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;

    const body = this.querySelector('.bnq-notify__body');
    const incomingBody = incoming.querySelector('.bnq-notify__body');
    if (body && incomingBody) body.textContent = incomingBody.textContent;
  };

  /** @param {KeyboardEvent} event */
  #handleEscape = (event) => {
    if (event.key !== 'Escape') return;
    if (this.refs.trigger.getAttribute('aria-expanded') !== 'true') return;

    event.stopPropagation();
    this.#toggle();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#handleSubmit();
  };

  #clearValidation = () => {
    if (this.#state !== 'invalid') return;
    this.refs.email.removeAttribute('aria-invalid');
    this.#setStatus('', 'idle');
  };

  #handleSubmit = async () => {
    // Guards every path into a second request: the rapid double-click, the Enter-then-click, and
    // the click after a terminal state.
    if (this.#inFlight) return;
    if (['success', 'duplicate'].includes(this.#state)) return;

    const { email, submit, submitLabel } = this.refs;
    const value = email.value.trim();

    if (!value || !EMAIL_PATTERN.test(value)) {
      email.setAttribute('aria-invalid', 'true');
      this.#setStatus(this.#message('error_email'), 'invalid');
      email.focus();
      return;
    }

    email.removeAttribute('aria-invalid');

    const endpoint = this.dataset.endpoint?.trim();

    // No provider connected. Say exactly that. Do not store, do not queue, do not confirm.
    if (!endpoint) {
      this.#setStatus(this.#message('unconfigured'), 'unconfigured');
      return;
    }

    this.#inFlight = true;
    this.#state = 'submitting';
    submit.disabled = true;
    const idleLabel = submitLabel?.textContent ?? '';
    if (submitLabel) submitLabel.textContent = this.#message('submitting');
    this.#setStatus('', 'submitting');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          productId: this.dataset.productId,
          // The product form's hidden `id` input is the value Horizon actually submits to the cart,
          // maintained by product-form.js on every selection. Preferring it over the transcribed
          // attribute means the request is bound to the same variant the customer would have
          // bought, even if a re-sync were ever missed.
          variantId: this.#formVariantId ?? this.dataset.variantId,
          email: value,
          locale: this.dataset.locale,
          market: this.dataset.market,
        }),
        signal: this.#controller.signal,
      });

      if (response.status === 409) {
        this.#setStatus(this.#message('duplicate'), 'duplicate');
        return;
      }

      if (!response.ok) {
        this.#setStatus(this.#message('error_generic'), 'error');
        return;
      }

      this.#setStatus(this.#message('success'), 'success');
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.#setStatus(this.#message('error_network'), 'error');
    } finally {
      this.#inFlight = false;
      submit.disabled = false;
      if (submitLabel) submitLabel.textContent = idleLabel;
    }
  };

  /**
   * @param {string} text
   * @param {string} state
   */
  #setStatus(text, state) {
    this.refs.status.textContent = text;
    this.#state = state;
  }

  /**
   * Copy is authored in `locales/en.default.json` and passed in as data attributes by the snippet,
   * so every message the customer sees is translated and merchant-editable. The map is explicit
   * rather than computed from the key, because a mis-derived dataset name would fail silently and
   * announce an empty string — the one failure mode a live region must not have.
   *
   * The English fallbacks are a last resort for a missing translation, not the primary source.
   *
   * @param {'error_email'|'error_generic'|'error_network'|'success'|'duplicate'|'unconfigured'|'submitting'} key
   * @returns {string}
   */
  #message(key) {
    /** @type {Record<string, [string, string]>} */
    const messages = {
      error_email: ['messageErrorEmail', 'Enter a valid email address.'],
      error_generic: ['messageErrorGeneric', 'We could not register your request. Please try again.'],
      error_network: ['messageErrorNetwork', 'Connection lost. Please check your connection and try again.'],
      success: ['messageSuccess', 'Done — we will email you when this comes back in stock.'],
      duplicate: ['messageDuplicate', 'You are already on the list for this item.'],
      unconfigured: ['messageUnconfigured', 'Stock alerts are not switched on for this store yet.'],
      submitting: ['messageSubmitting', 'Submitting…'],
    };

    const entry = messages[key];
    if (!entry) return '';

    return this.dataset[entry[0]]?.trim() || entry[1];
  }
}

if (!customElements.get('bnq-notify')) {
  customElements.define('bnq-notify', BnqNotify);
}
